import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Copy, Eye, EyeOff, Trash2, MoreVertical, RefreshCw, Shield, BarChart3 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const TENANT = { id: "org_nexus_002", name: "Nexus Financial", plan: "Enterprise", maxKeys: 50 };

const KEYS = [
  { id: "key_01", name: "Production Primary", key: "qe_live_nex_a1b2c3d4e5f6g7h8", prefix: "qe_live_nex_a1b2...h8", env: "Production", status: "active", created: "Sep 5, 2025", lastUsed: "5 min ago", expires: "Dec 5, 2025", permissions: ["random", "vrf", "health"], rps: 200, requests: "4,521,000", bandwidth: "168 GB" },
  { id: "key_02", name: "Production Secondary", key: "qe_live_nex_i9j0k1l2m3n4o5p6", prefix: "qe_live_nex_i9j0...p6", env: "Production", status: "active", created: "Sep 10, 2025", lastUsed: "10 min ago", expires: "Dec 10, 2025", permissions: ["random", "health"], rps: 200, requests: "2,100,000", bandwidth: "78 GB" },
  { id: "key_03", name: "VRF Dedicated", key: "qe_live_nex_q7r8s9t0u1v2w3x4", prefix: "qe_live_nex_q7r8...x4", env: "Production", status: "active", created: "Oct 1, 2025", lastUsed: "12 min ago", expires: "Jan 1, 2026", permissions: ["vrf"], rps: 100, requests: "1,200,000", bandwidth: "42 GB" },
  { id: "key_04", name: "Batch Processing", key: "qe_live_nex_y5z6a7b8c9d0e1f2", prefix: "qe_live_nex_y5z6...f2", env: "Production", status: "active", created: "Oct 15, 2025", lastUsed: "1 hour ago", expires: "Jan 15, 2026", permissions: ["random", "vrf"], rps: 500, requests: "380,000", bandwidth: "14 GB" },
  { id: "key_05", name: "Staging Primary", key: "qe_test_nex_g3h4i5j6k7l8m9n0", prefix: "qe_test_nex_g3h4...n0", env: "Staging", status: "active", created: "Nov 1, 2025", lastUsed: "3 hours ago", expires: "Feb 1, 2026", permissions: ["random", "vrf", "health", "stats"], rps: 50, requests: "45,000", bandwidth: "1.2 GB" },
  { id: "key_06", name: "Dev Testing", key: "qe_dev_nex_o1p2q3r4s5t6u7v8", prefix: "qe_dev_nex_o1p2...v8", env: "Development", status: "active", created: "Nov 15, 2025", lastUsed: "2 days ago", expires: "May 15, 2026", permissions: ["random", "vrf", "health", "stats"], rps: 10, requests: "1,200", bandwidth: "0.04 GB" },
  { id: "key_07", name: "Load Test Runner", key: "qe_test_nex_w9x0y1z2a3b4c5d6", prefix: "qe_test_nex_w9x0...d6", env: "Staging", status: "active", created: "Dec 1, 2025", lastUsed: "5 days ago", expires: "Mar 1, 2026", permissions: ["random"], rps: 1000, requests: "12,500", bandwidth: "0.5 GB" },
  { id: "key_08", name: "Legacy Integration", key: "qe_live_nex_e7f8g9h0i1j2k3l4", prefix: "qe_live_nex_e7f8...l4", env: "Production", status: "revoked", created: "Sep 3, 2025", lastUsed: "Nov 30, 2025", expires: "N/A", permissions: ["random"], rps: 100, requests: "890,000", bandwidth: "32 GB" },
];

const KEY_USAGE = [
  { date: "Feb 7", key_01: 680000, key_02: 310000, key_03: 180000 },
  { date: "Feb 9", key_01: 720000, key_02: 340000, key_03: 195000 },
  { date: "Feb 11", key_01: 810000, key_02: 380000, key_03: 220000 },
  { date: "Feb 13", key_01: 750000, key_02: 350000, key_03: 210000 },
];

const envColor = (e) => e === "Production" ? "bg-red-50 text-red-700 border-red-200" : e === "Staging" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-blue-50 text-blue-700 border-blue-200";

export default function TenantKeys() {
  const [showCreate, setShowCreate] = useState(false);
  const [showKey, setShowKey] = useState({});

  return (
    <div data-testid="tenant-keys-page">
      <div className="flex items-start justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link to="/admin/tenants/org_nexus_002" className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" data-testid="back-to-detail-btn">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="tenant-keys-title">API Key Management</h1>
              <Badge className="bg-slate-900 text-white text-[10px]">{TENANT.plan}</Badge>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">{TENANT.name} &middot; {KEYS.filter(k => k.status === 'active').length} active keys of {TENANT.maxKeys} max</p>
          </div>
        </div>
        <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 transition-colors text-xs font-bold uppercase tracking-widest px-5 py-2.5" data-testid="create-tenant-key-btn">
          <Plus className="w-4 h-4" /> New API Key
        </button>
      </div>

      {/* Usage by Key Chart */}
      <div className="bg-white border border-slate-200 p-6 mb-4" data-testid="key-usage-chart">
        <h3 className="text-sm font-semibold text-slate-900 mb-1">Request Volume by Key</h3>
        <p className="text-xs text-slate-500 mb-4">Top 3 active keys, last 7 days</p>
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={KEY_USAGE}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 0, fontSize: 12 }} />
            <Area type="monotone" dataKey="key_01" stroke="#06B6D4" strokeWidth={1.5} fill="none" name="Prod Primary" />
            <Area type="monotone" dataKey="key_02" stroke="#0F172A" strokeWidth={1.5} fill="none" name="Prod Secondary" />
            <Area type="monotone" dataKey="key_03" stroke="#64748B" strokeWidth={1.5} fill="none" name="VRF Dedicated" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white border border-slate-200" data-testid="tenant-keys-table">
        <div>
          <div className="grid grid-cols-[1fr_180px_80px_80px_180px_80px_90px_90px_40px] bg-slate-50 border-b border-slate-200">
            {["Name","Key","Env","Status","Permissions","RPS","Requests","Last Used",""].map((h,i) => (
              <div key={i} className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
            ))}
          </div>
          {KEYS.map((k, i) => (
            <div key={i} className="grid grid-cols-[1fr_180px_80px_80px_180px_80px_90px_90px_40px] border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`tenant-key-row-${i}`}>
              <div className="px-4 py-3 flex items-center">
                <div>
                  <div className="text-sm font-medium text-slate-900">{k.name}</div>
                  <div className="text-[10px] text-slate-400">Created {k.created}</div>
                </div>
              </div>
              <div className="px-4 py-3 flex items-center gap-1.5">
                <code className="text-[11px] font-mono text-slate-600 bg-slate-50 px-1.5 py-0.5 border border-slate-100">{showKey[k.id] ? k.key : k.prefix}</code>
                <button onClick={() => setShowKey(p => ({...p, [k.id]: !p[k.id]}))} className="p-0.5 text-slate-400 hover:text-slate-600" data-testid={`toggle-tkey-${i}`}>
                  {showKey[k.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                </button>
                <button className="p-0.5 text-slate-400 hover:text-slate-600" data-testid={`copy-tkey-${i}`}><Copy className="w-3 h-3" /></button>
              </div>
              <div className="px-4 py-3 flex items-center"><Badge variant="outline" className={`text-[9px] ${envColor(k.env)}`}>{k.env}</Badge></div>
              <div className="px-4 py-3 flex items-center"><Badge variant="outline" className={`text-[9px] uppercase ${k.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>{k.status}</Badge></div>
              <div className="px-4 py-3 flex items-center"><div className="flex gap-0.5 flex-wrap">{k.permissions.map(p => <Badge key={p} variant="outline" className="text-[8px] font-mono text-slate-500 border-slate-200">{p}</Badge>)}</div></div>
              <div className="px-4 py-3 font-mono text-xs text-slate-600 flex items-center">{k.rps}</div>
              <div className="px-4 py-3 font-mono text-xs text-slate-600 flex items-center">{k.requests}</div>
              <div className="px-4 py-3 text-xs text-slate-500 flex items-center">{k.lastUsed}</div>
              <div className="px-4 py-3 flex items-center">
                <DropdownMenu>
                  <DropdownMenuTrigger className="p-1 text-slate-400 hover:text-slate-600" data-testid={`tkey-actions-${i}`}>
                    <MoreVertical className="w-4 h-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem className="text-xs"><BarChart3 className="w-3 h-3 mr-2" />View Usage</DropdownMenuItem>
                    <DropdownMenuItem className="text-xs"><Shield className="w-3 h-3 mr-2" />Edit Permissions</DropdownMenuItem>
                    <DropdownMenuItem className="text-xs"><RefreshCw className="w-3 h-3 mr-2" />Rotate Key</DropdownMenuItem>
                    <DropdownMenuItem className="text-xs text-red-600"><Trash2 className="w-3 h-3 mr-2" />Revoke Key</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Key Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg" data-testid="create-tenant-key-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight">Create API Key for {TENANT.name}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Configure the key name, environment, permissions, and rate limits.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Key Name *</Label>
              <Input placeholder="e.g., Mobile App Backend" className="mt-1.5 h-10" data-testid="tkey-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Environment *</Label>
              <Select>
                <SelectTrigger className="mt-1.5 h-10" data-testid="tkey-env-select"><SelectValue placeholder="Select environment" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="production">Production</SelectItem>
                  <SelectItem value="staging">Staging</SelectItem>
                  <SelectItem value="development">Development</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Permissions</Label>
              <div className="space-y-2 mt-2">
                {[
                  { id: "p-random", label: "Random Bytes", path: "/v1/random/*" },
                  { id: "p-vrf", label: "VRF Service", path: "/v1/vrf/*" },
                  { id: "p-health", label: "Health Status", path: "/v1/health/*" },
                  { id: "p-stats", label: "Statistics", path: "/v1/stats/*" },
                ].map(p => (
                  <div key={p.id} className="flex items-center gap-3 p-2 border border-slate-100 hover:bg-slate-50">
                    <Checkbox id={p.id} defaultChecked={p.id === "p-random"} data-testid={`tkey-perm-${p.id}`} />
                    <div className="flex-1"><Label htmlFor={p.id} className="text-sm text-slate-900 cursor-pointer">{p.label}</Label><div className="text-[10px] text-slate-400 font-mono">{p.path}</div></div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Max Requests Per Second</Label>
              <Input type="number" defaultValue="100" className="mt-1.5 h-10 font-mono" data-testid="tkey-rps-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Key Expiry</Label>
              <Select defaultValue="90">
                <SelectTrigger className="mt-1.5 h-10" data-testid="tkey-expiry-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="180">180 days</SelectItem>
                  <SelectItem value="365">1 year</SelectItem>
                  <SelectItem value="never">Never (manual rotation only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 hover:bg-slate-50" data-testid="tkey-cancel-btn">Cancel</button>
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm bg-slate-900 text-white hover:bg-slate-800 uppercase tracking-wider font-bold" data-testid="tkey-create-btn">Generate Key</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
